import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import WishForm from './WishForm';
import WishBubble from './WishBubble';
import HeroBubbleIntro from './HeroBubbleIntro';
import { Sparkles, Search } from 'lucide-react';
import { supabase } from '../lib/supabase';
import Blobs from './Blobs';
import { useTheme } from '../context/ThemeContext';

export default function Profile({ dark }: { dark: boolean; setDark?: (v: boolean) => void }) {
  const [showWishForm, setShowWishForm] = useState(false);
  const [wishes, setWishes] = useState<any[]>([]);
  const [bubblePositions, setBubblePositions] = useState<{ left: string; top: string; z: number }[]>([]);
  const [search, setSearch] = useState('');
  const { designMode, adminDefaultDesignMode, setAdminDefaultDesignMode } = useTheme();
  const [savingDesign, setSavingDesign] = useState(false);
  const isModern = designMode === 'modern';

  // Wish bubbles stay hidden (in WishBubble) until the hero bubble intro
  // bursts, then all of them fall in together — tied to the hero pop
  // moment (via HeroBubbleIntro's onPop callback), not to scroll position,
  // so they aren't missed if the bubble section sits below the fold.
  const [bubblesInView, setBubblesInView] = useState(false);

  // Cek apakah admin login
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    setIsAdmin(!!localStorage.getItem('admin'));
  }, []);

  // Fetch wishes (recent, limit 7 for bubbles)
  useEffect(() => {
    const fetchWishes = async () => {
      let shouldFallback = false;
      try {
        const res = await fetch('/api/getWishes?offset=0&limit=7&order=created_at.desc');
        if (!res.ok) {
          // Only fallback if 404 or 5xx
          if (res.status === 404 || (res.status >= 500 && res.status < 600)) {
            shouldFallback = true;
            throw new Error('Function not available');
          } else {
            // For other errors, do not fallback
            return;
          }
        }
        const contentType = res.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          shouldFallback = true;
          throw new Error('Not a JSON response');
        }
        const data = await res.json();
        setWishes(data);
        setBubblePositions(
          data.map(() => ({
            left: `${Math.random() * 60 + 10}%`,
            top: `${Math.random() * 30 + 55}%`,
            z: Math.floor(Math.random() * 10) + 1,
          }))
        );
      } catch (error) {
        if (!shouldFallback) return;
        // Fallback: fetch direct from supabase (for local dev only if function truly unavailable)
        try {
          const { data, error: supaError } = await supabase
            .from('wishes')
            .select('id, content, created_at, status, author_name, display_name_type')
            .or('status.eq.0,status.eq.-1')
            .order('created_at', { ascending: false })
            .limit(7);
          if (supaError) throw supaError;
          // Privacy filter: samakan dengan Netlify Function
          const filtered = (data || []).map(wish => {
            let filteredName = wish.author_name;
            if (wish.display_name_type === 'anonymous') {
              filteredName = null;
            } else if (wish.display_name_type === 'censored') {
              filteredName = wish.author_name
                ? (wish.author_name as string).split(' ').map((w: string) => w[0] + '.').join(' ')
                : null;
            }
            return { ...wish, author_name: filteredName };
          });
          setWishes(filtered);
          setBubblePositions(
            filtered.map(() => ({
              left: `${Math.random() * 60 + 10}%`,
              top: `${Math.random() * 30 + 55}%`,
              z: Math.floor(Math.random() * 10) + 1,
            }))
          );
        } catch (err) {
          console.error('Error fetching wishes:', err);
        }
      }
    };
    fetchWishes();
  }, []);

  // Filter wishes by content (not author)
  const filteredWishes = wishes.filter(wish =>
    wish.content.toLowerCase().includes(search.toLowerCase())
  );

  // Map wishes to displayName (tidak perlu masking lagi, cukup tampilkan author_name)
  const mappedWishes = filteredWishes.map(wish => {
    return { ...wish, displayName: wish.author_name };
  });

  // Menu config for pastel cards and emoji
  const menu = [
    {
      label: 'Heartwork',
      to: '/interests',
      emoji: '🎮',
      bg: dark ? 'bg-[#232634]' : 'bg-[#f9e2af]',
      text: dark ? 'text-[#f9e2af]' : 'text-[#232634]',
      icon: null,
    },
    {
      label: 'Playground',
      to: '/challenges',
      emoji: '🌌', // changed from 🌈 to 🌌
      bg: dark ? 'bg-[#313244]' : 'bg-[#cdd6f4]',
      text: dark ? 'text-[#cdd6f4]' : 'text-[#232634]',
      icon: null,
    },
    {
      label: 'Diary',
      to: '/story',
      emoji: '📝',
      bg: dark ? 'bg-[#45475a]' : 'bg-[#a6e3a1]',
      text: dark ? 'text-[#232634]' : 'text-[#232634]',
      icon: null,
    },
  ];

  return (
    <div className={`relative flex flex-col items-center min-h-screen w-full pt-8 pb-8 md:pt-10 md:pb-10 font-rounded transition-colors bg-[#f6faff] dark:bg-[#181825] ${isModern ? 'modern-page modern-glow-bg' : ''}`}>
      <HeroBubbleIntro dark={dark} onPop={() => setBubblesInView(true)} />
      {/* Theme toggle & Logout button */}

      {/* Main Blobs Background (mockup style, responsive) */}
      <svg className="absolute -top-24 -left-16 w-[320px] h-[180px] md:w-[520px] md:h-[340px] opacity-60 animate-blob-float z-0" viewBox="0 0 520 340" fill="none" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="260" cy="170" rx="240" ry="120" fill="#b4befe" />
      </svg>
      <svg className="absolute -bottom-16 -right-10 w-[220px] h-[100px] md:w-[400px] md:h-[200px] opacity-50 animate-blob-float-slow z-0" viewBox="0 0 400 200" fill="none" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="200" cy="100" rx="180" ry="80" fill="#a6e3a1" />
      </svg>
      {/* Accent shapes (responsive) */}
      {/* Star moved to top-right, next to theme toggle */}
      {/* Geser star ke posisi lebih ke bawah dan kiri, sesuai mockup merah */}
      <svg className="absolute top-32 right-24 w-10 h-10 md:w-14 md:h-14 z-40" viewBox="0 0 48 48" fill="none"><polygon points="24,4 29,18 44,18 32,28 36,44 24,35 12,44 16,28 4,18 19,18" fill="#f9e2af"/></svg>
      <svg className="absolute bottom-24 left-8 w-6 h-6 md:w-8 md:h-8 z-10" viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#f38ba8"/></svg>
      <svg className="absolute bottom-10 right-1/3 w-4 h-4 md:w-6 md:h-6 z-10" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="12" fill="#89b4fa"/></svg>
      <svg className="absolute top-1/3 left-1/4 w-4 h-4 md:w-6 md:h-6 z-10" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="12" fill="#fab387"/></svg>
      {/* Blobs component for extra clouds */}
      <Blobs dark={dark} />
      {/* SLIDER ONLY: Custom dark/light mode switch at top-right (HIDE this, use only global from App.tsx) */}
      {/* <div className="fixed top-4 right-4 md:top-6 md:right-8 z-50"> ...theme toggle... </div> */}
      {/* Greeting */}
      <h1 className={`relative z-10 text-4xl md:text-5xl font-extrabold mb-3 md:mb-4 text-center ${isModern ? 'modern-title-gradient' : (dark ? 'text-[#f5e0dc]' : 'text-[#232634]')}`}>Hi, I’m Zhanto!</h1>
      <p className={`relative z-10 text-lg md:text-2xl mb-6 md:mb-8 text-center ${dark ? 'text-[#a6adc8]' : 'text-[#6c6f85]'}`}>
        Light notes about world, art, & thoughts yang suka nyasar akhirnya kesini<span className="inline-block"></span>
      </p>
      {/* Avatar */}
      <div className="relative z-10 mb-4 md:mb-6 flex flex-col items-center">
        <div className={isModern ? 'modern-avatar-ring' : ''}>
          <img
            src="/photo_profile.jpg"
            alt="Zhanto Avatar"
            className={`w-24 h-24 md:w-36 md:h-36 rounded-full border-4 md:border-8 ${dark ? 'border-[#232634]' : 'border-[#f9e2af]'} shadow-lg bg-[#232634] object-cover`}
          />
        </div>
        {/* Tombol Logout di bawah profile picture */}
        {isAdmin && (
          <button
            onClick={() => {
              localStorage.removeItem('admin');
              window.location.reload();
            }}
            className="mt-3 px-4 py-1 rounded-lg bg-red-500 hover:bg-red-600 text-white font-bold text-sm shadow transition-all"
            title="Logout"
          >
            Logout
          </button>
        )}
        {/* Admin-only: pilih tampilan default (New/Old) untuk semua pengunjung */}
        {isAdmin && (
          <div className="mt-3 flex flex-col items-center gap-1">
            <span className={`text-xs font-bold ${dark ? 'text-[#a6adc8]' : 'text-[#6c6f85]'}`}>
              Default tampilan situs
            </span>
            <div className="flex items-center gap-1 rounded-full bg-white/70 dark:bg-[#232634]/80 backdrop-blur-md shadow px-1 py-1 text-xs font-bold border border-white/30 dark:border-[#45475a]/60">
              <button
                type="button"
                disabled={savingDesign}
                onClick={async () => { setSavingDesign(true); await setAdminDefaultDesignMode('classic'); setSavingDesign(false); }}
                className={`px-3 py-1 rounded-full transition-colors disabled:opacity-50 ${adminDefaultDesignMode === 'classic' ? 'bg-[#b4befe] text-[#232634]' : 'text-[#6c6f85] dark:text-[#a6adc8]'}`}
              >
                Old
              </button>
              <button
                type="button"
                disabled={savingDesign}
                onClick={async () => { setSavingDesign(true); await setAdminDefaultDesignMode('modern'); setSavingDesign(false); }}
                className={`px-3 py-1 rounded-full transition-colors disabled:opacity-50 ${adminDefaultDesignMode === 'modern' ? 'bg-[#89b4fa] text-[#181825]' : 'text-[#6c6f85] dark:text-[#a6adc8]'}`}
              >
                New
              </button>
            </div>
          </div>
        )}
        {/* Social Links */}
        <div className="flex gap-3 mt-4">
          {/* X (Twitter) */}
          <a href="https://x.com/zhanto_moyzrano" target="_blank" rel="noopener noreferrer" title="X" className="group">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" className={`transition-colors ${dark ? 'text-white' : 'text-[#232634]'}`}> <path d="M17.53 3.5h3.71l-7.98 9.13L22 20.5h-6.2l-4.85-6.41-5.54 6.41H2.2l8.51-9.84L2 3.5h6.36l4.36 5.77 4.81-5.77Zm-1.09 15.13h1.03l-6.36-8.4-1.14-1.51H6.9l6.36 8.4 1.18 1.51Z" fill="currentColor"/></svg>
          </a>
          {/* LinkedIn */}
         {/* <a href="https://id.linkedin.com/in/shanto-moyrano-830a5b17a" target="_blank" rel="noopener noreferrer" title="LinkedIn" className="group">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" className={`transition-colors ${dark ? 'text-white' : 'text-[#232634]'}`}> <path d="M19 0h-14c-2.76 0-5 2.24-5 5v14c0 2.76 2.24 5 5 5h14c2.76 0 5-2.24 5-5v-14c0-2.76-2.24-5-5-5zm-11.75 20h-3v-10h3v10zm-1.5-11.25c-.97 0-1.75-.78-1.75-1.75s.78-1.75 1.75-1.75 1.75.78 1.75 1.75-.78 1.75-1.75 1.75zm15.25 11.25h-3v-5.5c0-1.32-.03-3-1.83-3-1.83 0-2.11 1.43-2.11 2.9v5.6h-3v-10h2.89v1.36h.04c.4-.75 1.37-1.54 2.82-1.54 3.01 0 3.57 1.98 3.57 4.56v5.62z" fill="currentColor"/></svg>
          </a> */}
          {/* Spotify (music) */}
          <a href="/playlists" title="Spotify" className="group focus:outline-none">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" className={`transition-colors ${dark ? 'text-white' : 'text-[#232634]'}`}> <circle cx="12" cy="12" r="10" fill={dark ? '#232634' : '#b4befe'} /><path d="M17.25 16.13a.75.75 0 0 1-1.03.23c-2.82-1.73-6.39-2.12-10.6-1.15a.75.75 0 1 1-.33-1.46c4.56-1.04 8.5-.6 11.6 1.25.36.22.47.69.23 1.03Zm1.5-2.75a.94.94 0 0 1-1.29.29c-3.23-2-8.16-2.6-11.97-1.41a.94.94 0 1 1-.56-1.8c4.23-1.32 9.6-.67 13.23 1.59.44.27.57.85.29 1.29Zm.13-2.81C15.1 8.2 8.9 8.01 6.02 8.8a1.13 1.13 0 1 1-.54-2.2c3.36-.82 10.13-.6 13.02 2.01a1.13 1.13 0 0 1-1.62 1.76Z" fill={dark ? '#a6e3a1' : '#232634'} /></svg>
          </a>
        </div>
      </div>
      {/* Menu Cards (responsive, below avatar/greeting) */}
      <div className="relative flex flex-col md:flex-row gap-6 md:gap-8 mb-12 md:mb-20 z-10 w-full max-w-2xl justify-center items-center">
        {menu.map((item) => (
          <Link
            key={item.label}
            to={item.to}
            className={`flex flex-col items-center justify-center rounded-2xl px-8 py-8 md:px-12 md:py-10 shadow-lg hover:scale-105 transition-all font-extrabold text-lg md:text-xl ${isModern ? 'modern-menu-card' : (dark ? 'bg-gray-800/80 text-white' : 'bg-gray-100/80 text-[#232634]')}`}
            style={{ minWidth: 180, minHeight: 140, maxWidth: 240 }}
          >
            {isModern ? (
              <span className="modern-menu-icon text-3xl">{item.emoji}</span>
            ) : (
              <span className="text-4xl md:text-5xl mb-3">{item.emoji}</span>
            )}
            <span className="text-xl md:text-2xl font-bold mb-1">{item.label}</span>
          </Link>
        ))}
      </div>
      {/* Create a Bubble Button */}
      <button
        onClick={() => setShowWishForm(true)}
        className={`font-bold py-3 px-8 md:px-10 rounded-full shadow-lg mb-4 flex items-center gap-2 transition-all text-base
          ${dark
            ? 'bg-[#89b4fa] text-[#181825] hover:bg-[#b4befe]'
            : 'bg-[#b4befe] text-[#232634] hover:bg-[#a6e3a1]'
        } ${isModern ? 'modern-btn' : ''}`}
        style={{ letterSpacing: 0.5 }}
      >
        <Sparkles className="text-yellow-300" size={18} />
        Create a Bubble
      </button>
      {/* Search Bar */}
      <div className={`relative w-full max-w-xs md:max-w-lg mb-8 md:mb-10 z-10 ${isModern ? 'modern-search-wrap' : ''}`}>
        {isModern && <Search className="modern-search-icon" size={18} />}
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search bubbles..."
          className={`w-full px-4 py-2 md:px-5 md:py-2 rounded-full border-none outline-none bg-white dark:bg-[#232634] text-base ${dark ? 'text-white placeholder:text-[#a6adc8]' : 'text-[#232634] placeholder:text-[#6c6f85]'} shadow ${isModern ? 'modern-input' : ''}`}
        />
      </div>
      {/* Floating Bubbles */}
      <div className="relative w-full h-[260px] md:h-[400px] mb-10 md:mb-16 z-10">
        {mappedWishes.map((wish, i) => (
          <div
            key={wish.id}
            className="absolute"
            style={{
              left: bubblePositions[i]?.left,
              top: bubblePositions[i]?.top,
              zIndex: bubblePositions[i]?.z,
              pointerEvents: showWishForm ? 'none' : 'auto',
              transition: 'left 0.5s, top 0.5s',
            }}
          >
            <WishBubble
              id={wish.id}
              content={wish.content}
              authorName={wish.displayName}
              displayType={wish.display_name_type}
              timestamp={wish.created_at}
              dark={dark}
              modern={isModern}
              hideAvatar
              startAnimation={bubblesInView}
            />
          </div>
        ))}
      </div>
      {/* Wish Form Modal */}
      {showWishForm && (
        <WishForm
          onClose={() => setShowWishForm(false)}
          onWishCreated={() => {/* refetch wishes */}}
          dark={dark}
          modern={isModern}
        />
      )}
    </div>
  );
}

/* 
Tambahkan ke src/index.css (atau tailwind.css):

@keyframes blobFloat1 {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-12px) scale(1.03); }
}
@keyframes blobFloat2 {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(16px) scale(1.04); }
}
.animate-\[blobFloat1_12s_ease-in-out_infinite\] {
  animation: blobFloat1 12s ease-in-out infinite;
}
.animate-\[blobFloat2_14s_ease-in-out_infinite\] {
  animation: blobFloat2 14s ease-in-out infinite;
}
*/